import {
  useMfCentralStatus,
  useGenerateMfCentralOtpMutation,
  useMfCentralV2RequestMutation,
  useMfCentralV2UploadQrMutation,
  useMfCentralImportState,
} from "creso-core/hooks";
import { Button, V2Label, V2Input, cn, V2Loader } from "web-design-system";
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ClipboardIcon, Loader2Icon, XIcon } from "lucide-react";
import { z } from "zod";
import { phoneNumberSchema } from "creso-core/zod";
import { toast } from "sonner";
import { Client } from "creso-core/types";
import { getPromiseWithResolvers, isNRI } from "creso-core/utils";
import { SyncingLottieAnimation } from "../indicator/syncing";
import { TimeoutLottieAnimation } from "../indicator/timeout";
import { handleError } from "../../utils/error";
import { FileUpload } from "./file-upload";

type MFCentralHoldingImportState =
  | "enter-otp"
  | "update-contact"
  | "upload-qr"
  | "otp-verified"
  | "syncing";

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
  });

export function MfCentralImport(props: {
  client: Client;
  /** Called once the import finishes, or when the user dismisses a stalled sync. */
  onClose: () => void;
  /** Omit to hide the CAS upload fallback (web-clients does not offer it). */
  onImportViaCas?: () => void;
}) {
  const { client, onClose, onImportViaCas } = props;

  const [importState, setImportState] =
    useState<MFCentralHoldingImportState>("update-contact");

  const {
    data: mfCentralStatus,
    isLoading: isLoadingMfCentralStatus,
    refetch,
  } = useMfCentralStatus(client?.investment_account ?? "");

  const uiImportState = useMfCentralImportState(mfCentralStatus);

  const { isPending: isGeneratingOtp } = useGenerateMfCentralOtpMutation(
    client?.investment_account ?? ""
  );

  // Tracks the last inputed contact info
  // Only clear this value when the user hits the back button
  // or when the attempt fails. Do not clear it when trying to generate an OTP.
  // Use email for NRI client - Fallback to phone number
  const [contactInfo, setContactInfo] = useState(
    (isNRI(client) ? client?.email : client?.phone_number) ||
      client?.phone_number ||
      ""
  );

  // Track if this is the first focus (when dialog opens)
  const isFirstFocusRef = useRef(true);

  const validateContactInfo = async (contactInfo: string) => {
    const trimmedContactInfo = contactInfo?.trim();
    // Check if all the entered digits are numbers.
    // Replace + which could be there as part of country code.
    const { success: isNumber } = z
      .string()
      .regex(/^\d+$/)
      .safeParse(trimmedContactInfo?.replace("+", ""));

    if (isNumber) {
      const { success: isPhoneNumber } =
        phoneNumberSchema.safeParse(trimmedContactInfo);
      if (!isPhoneNumber) {
        setValidationError("Please enter a valid phone number");
        return false;
      }
    } else {
      const { success: isEmail } = z
        .string()
        .email()
        .safeParse(trimmedContactInfo);
      if (!isEmail) {
        setValidationError("Please enter a valid email");
        return false;
      }
    }
    return true;
  };

  const mfcentralRequest = useMfCentralV2RequestMutation();
  const { mutateAsync: uploadQr, isPending: isUploadingQr } =
    useMfCentralV2UploadQrMutation();

  const [qrImageBase64, setQrImageBase64] = useState<string | null>(null);
  const [qrPreviewUrl, setQrPreviewUrl] = useState<string | null>(null);

  const handlePasteFromClipboard = async () => {
    try {
      const items = await navigator.clipboard.read();
      const imageItem = items.find((item) =>
        item.types.some((t) => t.startsWith("image/"))
      );
      if (!imageItem) {
        toast.error("No image found in clipboard");
        return;
      }
      const imageType = imageItem.types.find((t) => t.startsWith("image/"))!;
      const blob = await imageItem.getType(imageType);
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => {
        const result = reader.result as string;
        setQrPreviewUrl(result);
        setQrImageBase64(result.split(",")[1]);
      };
    } catch {
      toast.error(
        "Failed to read clipboard. Please try uploading the file instead."
      );
    }
  };

  const handleUploadQr = async () => {
    if (!qrImageBase64) return;
    try {
      await uploadQr({
        investmentAccountId: client.investment_account ?? "",
        qr_code: qrImageBase64,
      });
      await refetch();
      setImportState("syncing");
    } catch (error) {
      handleError(error, "Failed to upload QR code. Please try again.");
    }
  };

  const [hasImportError, setHasImportError] = useState("");
  const [mfCentralIframeUrl, setMfCentralIframeUrl] = useState<string | null>(
    null
  );

  const handleMfCentralV2Import = async (contactInfo: string) => {
    const isValid = await validateContactInfo(contactInfo);
    setHasImportError("");
    if (!isValid) return;
    try {
      const { redirect_url: redirectUrl } = await mfcentralRequest.mutateAsync({
        investmentAccountId: client?.investment_account ?? "",
        contactInfo,
      });
      if (!redirectUrl) {
        throw new Error("Redirection failed");
      }
      setMfCentralIframeUrl(redirectUrl);
    } catch (error) {
      setHasImportError("failed");
      handleError(error, "Failed to import holdings. Please try again later.");
    }
  };

  const [isQRUploaded, setIsQRUploaded] = useState(false);

  const onMfcCasComplete = useCallback(async () => {
    await refetch();
    setMfCentralIframeUrl(null);
    setIsQRUploaded(false);
    setImportState("syncing");
  }, [refetch]);

  const handleMessage = useCallback(
    async (event: MessageEvent) => {
      try {
        const message =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (message.type === "mfc-cas-download" && message.data?.base64) {
          await uploadQr({
            investmentAccountId: client?.investment_account ?? "",
            qr_code: message.data.base64,
          });
          setIsQRUploaded(true);
        } else if (message.type === "mfc-cas-complete") {
          if (!isQRUploaded) {
            const items = await navigator.clipboard.read();
            const imageItem = items.find((item) =>
              item.types.some((t) => t.startsWith("image/"))
            );
            if (!imageItem) {
              toast.error("No image found in clipboard");
              await onMfcCasComplete();
              return;
            }
            const imageType = imageItem.types.find((t) =>
              t.startsWith("image/")
            );
            const {
              promise: readerPromise,
              resolve,
              reject,
            } = getPromiseWithResolvers();
            const blob = await imageItem.getType(imageType as string);
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onload = async () => {
              const result = reader.result as string;
              await uploadQr({
                investmentAccountId: client?.investment_account ?? "",
                qr_code: result.split(",")[1],
              });
              setIsQRUploaded(true);
              resolve(true);
            };
            reader.onerror = () => {
              const err =
                "Failed to read clipboard. Please try uploading the file instead.";
              toast.error(err);
              reject(new Error(err));
            };
            await readerPromise;
          }
          await onMfcCasComplete();
        }
      } catch {
        // ignore non-JSON messages
      }
    },
    [client?.investment_account, uploadQr, onMfcCasComplete, isQRUploaded]
  );

  useEffect(() => {
    return () => {
      setMfCentralIframeUrl(null);
      setIsQRUploaded(false);
    };
  }, []);

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleMessage]);

  // This effect is to autoclose this dialog once the sync is complete
  useEffect(() => {
    if (importState === "syncing") {
      if (mfCentralStatus?.status === "SUCCESS") {
        onClose();
      }
    }
  }, [importState, mfCentralStatus?.status, onClose]);

  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    let timerId: NodeJS.Timeout;
    if (importState === "otp-verified") {
      timerId = setTimeout(() => {
        setImportState("syncing");
      }, 3000);
    }
    return () => timerId && clearTimeout(timerId);
  }, [importState]);

  return (
    <div
      className={cn(
        "relative flex flex-col flex-1",
        mfCentralIframeUrl ? "w-150 min-h-[90vh]" : "w-100"
      )}
    >
      {mfCentralIframeUrl && (
        <div className="absolute inset-0 z-50 flex flex-col bg-background">
          <div className="flex items-center justify-between px-4 py-2 border-b shrink-0">
            <span className="text-sm font-medium">MF Central</span>
            <Button
              size="icon"
              variant="ghost"
              className="-mr-2"
              onClick={() => setMfCentralIframeUrl(null)}
            >
              <XIcon className="size-4" />
            </Button>
          </div>
          <iframe
            allow="clipboard-write"
            src={mfCentralIframeUrl}
            className="flex-1 w-full border-none"
            title="MF Central"
            style={{ zoom: 0.85 }}
          />
        </div>
      )}
      <V2Loader isLoading={isLoadingMfCentralStatus}>
        {importState === "update-contact" && (
          <div className="flex flex-col flex-1 p-6">
            <h5 className="text-lg font-semibold">
              {hasImportError ? "Holdings import failed" : "Import holdings"}
            </h5>
            <span className="text-muted-foreground text-sm mt-2">
              {hasImportError
                ? `No holdings found for ${client?.name} against the ${
                    z.string().email().safeParse(contactInfo).success
                      ? "email"
                      : "phone number"
                  } ${contactInfo || client?.phone_number}`
                : "You will be redirected to MFCentral to enter OTP and download QR Code."}
            </span>
            <V2Label className="mt-8 mb-2 font-light">
              Enter {hasImportError ? "different" : ""} phone number or email
            </V2Label>
            <V2Input
              placeholder="Enter phone or email"
              value={contactInfo}
              onChange={(e) => {
                setContactInfo(e.target.value);
                setValidationError("");
              }}
              onFocus={(e) => {
                // Only prevent selection on the first focus (when dialog opens)
                if (isFirstFocusRef.current) {
                  isFirstFocusRef.current = false;
                  // Move cursor to end instead of selecting all text
                  // Use double requestAnimationFrame to ensure this runs after browser's default selection behavior
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      const { length } = e.target.value;
                      e.target.setSelectionRange(length, length);
                    });
                  });
                }
              }}
            />
            <span className="mt-1 text-destructive text-xs">
              {validationError}
            </span>
            <Button
              disabled={isGeneratingOtp}
              className="w-full mt-auto"
              onClick={() => handleMfCentralV2Import(contactInfo)}
            >
              Continue
            </Button>
            {onImportViaCas && (
              <Button
                onClick={onImportViaCas}
                variant="ghost"
                className="w-full mt-2"
              >
                Import via CAS upload
              </Button>
            )}
          </div>
        )}

        {importState === "upload-qr" && (
          <div className="flex flex-col flex-1 p-6 pt-5">
            <div className="flex flex-row gap-2 items-center">
              <Button
                onClick={() => {
                  setImportState("update-contact");
                  setValidationError("");
                  setQrImageBase64(null);
                  setQrPreviewUrl(null);
                  isFirstFocusRef.current = true;
                }}
                disabled={isUploadingQr}
                size="icon"
                variant="ghost"
                className={cn("-ml-3")}
              >
                <ArrowLeft />
              </Button>
              <h5 className="text-lg font-semibold">Upload QR Code</h5>
            </div>
            <span className="text-muted-foreground text-sm mt-2">
              Upload or paste the QR code image to authorize the import.
            </span>
            <div className="my-6 flex flex-col flex-1 gap-4">
              {qrPreviewUrl ? (
                <div className="flex flex-col flex-1 items-center gap-3">
                  <img
                    src={qrPreviewUrl}
                    alt="QR Code"
                    className="max-h-52 rounded-lg border object-contain"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setQrImageBase64(null);
                      setQrPreviewUrl(null);
                    }}
                  >
                    <XIcon className="size-4" />
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="h-64 flex gap-4 flex-col">
                  <FileUpload
                    acceptedTypes={["image/*"]}
                    onChange={async (file) => {
                      if (file) {
                        const base64 = await fileToBase64(file);
                        setQrImageBase64(base64);
                        setQrPreviewUrl(URL.createObjectURL(file));
                      } else {
                        setQrImageBase64(null);
                        setQrPreviewUrl(null);
                      }
                    }}
                  />
                  <div className="h-px bg-border relative">
                    <span className="text-sm bg-card absolute text-center px-3 left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2">
                      Or
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handlePasteFromClipboard}
                    className="w-full"
                  >
                    <ClipboardIcon className="size-4" />
                    Paste QR from Clipboard
                  </Button>
                </div>
              )}
            </div>
            <Button
              onClick={handleUploadQr}
              disabled={!qrImageBase64 || isUploadingQr}
              className="w-full mt-auto"
            >
              {isUploadingQr && <Loader2Icon className="animate-spin" />}
              {isUploadingQr ? "Uploading..." : "Upload QR Code"}
            </Button>
          </div>
        )}

        {importState === "syncing" && (
          <div className="flex flex-col flex-1 p-6">
            <h5 className="text-lg font-semibold">
              {uiImportState === "expired"
                ? "Taking longer than usual"
                : "Importing Funds"}
            </h5>
            <span className="text-muted-foreground text-sm mt-2">
              External portfolio should appear on the platform in a few mins.
            </span>
            <div className="flex flex-col items-center pt-12 flex-1 justify-start">
              {uiImportState === "expired" ? (
                <TimeoutLottieAnimation loop={false} height={150} width={150} />
              ) : (
                <SyncingLottieAnimation loop height={150} width={150} />
              )}
            </div>
            {uiImportState === "expired" && (
              <Button className="w-full mt-auto" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        )}
      </V2Loader>
    </div>
  );
}
